import { useState } from 'react';
import { useOrderStore, useResourceStore } from '@/store';
import { Order } from '@/types';

interface OrderFormProps {
  onClose: () => void;
}

export function OrderForm({ onClose }: OrderFormProps) {
  const addOrder = useOrderStore((state) => state.addOrder);
  const products = useResourceStore((state) => state.products);

  const [formData, setFormData] = useState({
    productId: '',
    productName: '',
    totalQuantity: 100,
    customer: '',
    batchNo: '',
    orderDate: new Date().toISOString().slice(0, 10),
    plannedDeliveryDate: '',
    progress: 0,
    status: '待處理' as Order['status'],
    note: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newOrder: Order = {
      id: `WO-${Date.now()}`,
      ...formData,
      plannedDeliveryDate: new Date(formData.plannedDeliveryDate).toISOString(),
      orderDate: new Date(formData.orderDate).toISOString(),
      actualDeliveryDate: null,
      mainWorkOrders: [],
    };

    addOrder(newOrder);
    onClose();
  };

  const handleProductChange = (productId: string) => {
    const product = products.find((p) => p.id === productId);
    if (product) {
      setFormData({
        ...formData,
        productId,
        productName: product.name,
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">新增訂單</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* 產品選擇 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              產品 <span className="text-red-500">*</span>
            </label>
            <select
              required
              value={formData.productId}
              onChange={(e) => handleProductChange(e.target.value)}
              className="input"
            >
              <option value="">請選擇產品</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.id} - {product.name} ({product.specification})
                </option>
              ))}
            </select>
          </div>

          {/* 客戶名稱 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              客戶名稱 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.customer}
              onChange={(e) => setFormData({ ...formData, customer: e.target.value })}
              className="input"
              placeholder="輸入客戶名稱"
            />
          </div>

          {/* 批號 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              批號 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.batchNo}
              onChange={(e) => setFormData({ ...formData, batchNo: e.target.value })}
              className="input"
              placeholder="輸入批號"
            />
          </div>

          {/* 數量 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              數量 <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              required
              min="1"
              value={formData.totalQuantity}
              onChange={(e) => setFormData({ ...formData, totalQuantity: Number(e.target.value) })}
              className="input"
            />
          </div>

          {/* 接單日期 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              接單日期 <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              required
              value={formData.orderDate}
              onChange={(e) => setFormData({ ...formData, orderDate: e.target.value })}
              className="input"
            />
          </div>

          {/* 預計交期 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              預計交期 <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              required
              value={formData.plannedDeliveryDate}
              onChange={(e) => setFormData({ ...formData, plannedDeliveryDate: e.target.value })}
              className="input"
            />
          </div>

          {/* 備註 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">備註</label>
            <textarea
              value={formData.note}
              onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              className="input"
              rows={3}
              placeholder="輸入備註事項"
            />
          </div>

          {/* 按鈕 */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              取消
            </button>
            <button
              type="submit"
              className="btn-primary"
            >
              新增訂單
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
